import { useEffect, useState } from 'react';
import { Routes, Route, NavLink, Link } from 'react-router-dom';
import Home from './pages/Home';
import Analyzer from './pages/Analyzer';
import Sessions from './pages/Sessions';
import Library from './pages/Library';
import Pros from './pages/Pros';
import { api } from './lib/api';

export default function App() {
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.me().then(setMe).catch(e => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <h1 className="display text-2xl text-cream-100 mb-2">Can't reach the API</h1>
          <p className="text-sm text-ink-400">{error}</p>
          <p className="text-xs text-ink-500 mt-4">
            If you're running locally, make sure <code className="monospace text-cream-300">wrangler dev</code> is running in the worker folder.
          </p>
        </div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-ink-400 text-sm">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Nav me={me} />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home me={me} />} />
          <Route path="/analyze" element={<Analyzer />} />
          <Route path="/analyze/:sessionId" element={<Analyzer />} />
          <Route path="/sessions" element={<Sessions />} />
          <Route path="/library" element={<Library />} />
          <Route path="/pros" element={<Pros isAdmin={me.isAdmin} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function Nav({ me }) {
  const linkBase = "text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded transition";
  const linkClass = ({ isActive }) =>
    `${linkBase} ${isActive ? 'text-cream-100 bg-fairway-800' : 'text-ink-400 hover:text-ink-100'}`;

  return (
    <header className="border-b border-fairway-800 bg-fairway-950/80 backdrop-blur sticky top-0 z-40">
      <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 mr-auto">
          <Logo />
          <span className="display text-cream-100 text-lg font-medium tracking-tight">Ekko</span>
        </Link>
        <nav className="flex items-center gap-1">
          <NavLink to="/analyze" className={linkClass}>Analyze</NavLink>
          <NavLink to="/sessions" className={linkClass}>Sessions</NavLink>
          <NavLink to="/library" className={linkClass}>Library</NavLink>
          <NavLink to="/pros" className={linkClass}>Pros</NavLink>
        </nav>
        <div className="text-[10px] monospace text-ink-500 hidden md:block">{me.email}</div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-fairway-800 py-4 mt-8">
      <div className="max-w-[1400px] mx-auto px-4 text-[10px] uppercase tracking-[0.2em] text-ink-500 flex justify-between">
        <span>Ekko · ekko.golf</span>
        <span className="monospace">prototype build</span>
      </div>
    </footer>
  );
}

function Logo() {
  return (
    <svg width="28" height="28" viewBox="0 0 32 32" className="flex-shrink-0">
      <rect width="32" height="32" rx="6" fill="#1c2217"/>
      <path d="M 8 8 L 8 24 M 8 16 L 14 8 M 8 16 L 14 24" stroke="#ebe4cd" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
      <path d="M 24 8 L 24 24 M 24 16 L 18 8 M 24 16 L 18 24" stroke="#ebe4cd" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
      <circle cx="16" cy="16" r="1.4" fill="#d14324"/>
    </svg>
  );
}

function NotFound() {
  return (
    <div className="max-w-md mx-auto text-center p-8">
      <h1 className="display text-3xl text-cream-100">Not found</h1>
      <p className="text-sm text-ink-400 mt-2">That page doesn't exist.</p>
      <Link to="/" className="inline-block mt-4 text-sm text-cream-300 hover:underline">← Home</Link>
    </div>
  );
}
